import { useEffect, useRef, useCallback } from 'react';

const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const LAST_ACTIVITY_KEY = 'kairos_last_activity';
const IDLE_RETURN_PATH_KEY = 'kairos_idle_return_path';
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'];

/**
 * useIdleSignout — signs user out after 10 minutes of inactivity.
 * Also detects stale sessions when the app is brought back from background
 * (e.g. switching back to a browser tab or reopening a PWA).
 *
 * @param {Function} signOut  — signOut callback from useAuth
 * @param {boolean}  enabled  — only active when user is authenticated (not guest)
 */
export function useIdleSignout(signOut, enabled) {
  const timerRef = useRef(null);

  const stampActivity = useCallback(() => {
    try { localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString()); } catch {}
  }, []);

  const idleSignOut = useCallback(() => {
    try {
      const path = window.location.pathname + window.location.search;
      if (path && path !== '/') localStorage.setItem(IDLE_RETURN_PATH_KEY, path);
    } catch {}
    signOut();
  }, [signOut]);

  const resetTimer = useCallback(() => {
    stampActivity();
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (enabled) idleSignOut();
    }, IDLE_TIMEOUT_MS);
  }, [idleSignOut, enabled, stampActivity]);

  useEffect(() => {
    if (!enabled) return;

    // Check if session is already stale on mount (app reopened after being idle)
    try {
      const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      if (last > 0 && Date.now() - last > IDLE_TIMEOUT_MS) {
        idleSignOut();
        return;
      }
    } catch {}

    // Start idle timer
    resetTimer();

    // Reset on user activity
    const onActivity = () => resetTimer();
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    // Handle tab/app visibility changes (returning from background)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        try {
          const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
          if (last > 0 && Date.now() - last > IDLE_TIMEOUT_MS) {
            idleSignOut();
            return;
          }
        } catch {}
        resetTimer();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, idleSignOut, resetTimer]);
}

export { IDLE_RETURN_PATH_KEY };
